<template>
  <nav v-if="!disablePagination" class="flex flex-wrap items-center justify-between gap-3 px-0 py-0 text-[13px] md:w-full">
    <Flex gap-3 items-center class="min-w-1/2">
      <div class="text-slate-500">
        Exibindo <b>{{ rangeStart }}</b> a <b>{{ rangeEnd }}</b> de
        <b>{{ totalItems }}</b> itens.
      </div>
      <slot name="left" />
    </Flex>
    <Flex gap-4 items-center justify-end class="min-w-1/2">
      <SelectTable v-model="modelLimit" :items="statusItems" up class="w-28" placeholder="" v-if="totalItems > 20" />

      <ul v-if="totalPages > 1" class="inline-flex items-center rounded-[4px] border border-[#e5e7eb] divide-x divide-[#e5e7eb] text-[#5F6368]">
        <li :class="{ [disableClass]: currentPage == 1 }" @click="previousPage">
          <NavButton class="!h-8 !min-w-[32px] !px-2 rounded-s-[4px]" icon="fa-solid fa-chevron-left text-[12px]" />
        </li>

        <div v-if="totalPages < 4" class="flex items-center divide-x divide-[#e5e7eb]">
          <li v-for="i in totalPages" :key="i" @click="goToPage(i)">
            <NavButton :class="[`${currentPage === i ? activeClass : borderClass}`]">
              {{ i }}
            </NavButton>
          </li>
        </div>

        <div v-else class="flex items-center divide-x divide-[#e5e7eb]">
          <li @click="goToPage(1)">
            <NavButton :class="[`${currentPage == 1 ? activeClass : borderClass}`]">1</NavButton>
          </li>

          <li v-if="currentPage > 3" class="relative">
            <NavButton :class="borderClass">...</NavButton>
          </li>

          <li v-if="currentPage > 2" @click="goToPage(currentPage - 1)">
            <NavButton :class="borderClass">
              {{ currentPage - 1 }}
            </NavButton>
          </li>

          <li v-if="currentPage !== 1 && currentPage !== totalPages" @click="goToPage(currentPage)">
            <NavButton :class="activeClass">
              {{ currentPage }}
            </NavButton>
          </li>

          <li v-if="currentPage < totalPages - 1" @click="goToPage(currentPage + 1)">
            <NavButton :class="borderClass">
              {{ currentPage + 1 }}
            </NavButton>
          </li>

          <li v-if="currentPage < totalPages - 2" class="relative">
            <NavButton :class="borderClass">...</NavButton>
          </li>

          <li v-if="lastPage" @click="goToPage(lastPage)">
            <NavButton :class="[`${currentPage == lastPage ? activeClass : borderClass}`]">
              {{ lastPage }}
            </NavButton>
          </li>
        </div>
        <li :class="{ [disableClass]: currentPage == lastPage }" @click="nextPage">
          <NavButton class="!h-8 !min-w-[32px] !px-2 rounded-e-[4px]" icon="fa-solid fa-chevron-right text-[12px]" />
        </li>
      </ul>
    </Flex>
  </nav>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'

const emit = defineEmits(['update:modelValue', 'update:limit'])

const props = defineProps({
  disablePagination: {
    type: Boolean,
    default: false,
  },
  limit: { type: Number, default: 10 },
  totalItems: Number,
  totalPages: Number,
  modelValue: {
    type: Number,
    default: 1,
  },
})

const activeClass = ref('bg-slate-100 font-semibold text-[#13161b]')
const borderClass = ref('bg-white text-[#5F6368] hover:bg-slate-50')
const currentPage = ref(1)
const searchPageValue = ref(null)
const disableClass = ref('opacity-50')
const modelLimit = ref(props.limit)

const statusItems = ref([
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: '200', value: 200 },
  { label: '500', value: 500 },
])

const rangeStart = computed(() => {
  if (!props.totalItems) {
    return 0
  }

  return (currentPage.value - 1) * props.limit + 1
})

const rangeEnd = computed(() => {
  if (!props.totalItems) {
    return 0
  }

  return Math.min(currentPage.value * props.limit, props.totalItems)
})

const goToPage = (pageNumber) => {
  let page = Number(searchPageValue.value ?? pageNumber)

  if (!Number.isFinite(page) || page <= 0 || page > props.totalPages) {
    page = 1
  }

  currentPage.value = page
  searchPageValue.value = null
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    currentPage.value++
  }
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const lastPage = computed(() => {
  return props.totalPages
})

watch(
  () => props.modelValue,
  (newVal) => {
    currentPage.value = newVal
  },
)

watch(currentPage, (page) => {
  emit('update:modelValue', page)
})

watch(
  () => modelLimit.value,
  (newValue) => {
    emit('update:limit', newValue)
  },
)

onMounted(() => {
  currentPage.value = props.modelValue
})
</script>
